import {ArrayChange, diffArrays} from 'diff';
import {ParagraphElementType} from 'types';
import {RegDoc} from '../CompareVersionPanel/ComparisonComponent/ComparisonComponent.types';

type QueryParagraph = RegDoc['paragraphs'][number];

type QueryElement = QueryParagraph['elements'][number];

interface Element extends QueryElement {
	// This type is more specific than that of the query
	asset?: {
		uri: string;
	} | null;
}

export interface IParagraphCompareInput
	extends Pick<QueryParagraph, 'elements' | 'keywordAssignments'> {
	elements: Element[];
	enumeration: string;
	level: number;
	page: number;
}

export interface ComparableAsset {
	assetId: string;
	uri: string;
	/**
	 * The paragraph text elements' text will be aggregated into a single
	 * string. This is the character index that the asset should be placed after.
	 */
	index: number;
}

export interface IComparable extends Pick<IParagraphCompareInput, 'elements'> {
	enumeration: string;
	text: string;
	assets: Array<ComparableAsset>;
	[key: string]: any;
}

export enum CompareResult {
	Equal,
	New,
	Deleted,
	Changed,
	InReview,
}

export interface IComparedParagraph {
	paragraph: IComparable;
	textElements: ITextCompareElement[];
}

export interface IComparedItem {
	oldParagraph?: IParagraphCompareInput;
	newParagraph?: IParagraphCompareInput;
	resultType: CompareResult;
}

export interface ITextCompareElement {
	text: string;
	/**
	 * Whether or not the text exists in the current paragraph while not existing
	 * in the other paragraph.
	 */
	isChange: boolean;
}

export interface ITextCompareResult {
	oldParagraphModified: IParagraphCompareInput;
	newParagraphModified: IParagraphCompareInput;
	resultType: CompareResult;
}

export function cmpText(
	oldParagraph: IParagraphCompareInput,
	newParagraph: IParagraphCompareInput,
): ITextCompareResult {
	if (
		oldParagraph.elements.find(getIfElementHasTableOrImage) ||
		newParagraph.elements.find(getIfElementHasTableOrImage)
	) {
		return {
			oldParagraphModified: oldParagraph,
			newParagraphModified: newParagraph,
			resultType: CompareResult.InReview,
		};
	}

	const comparatorText = (left: Element, right: Element): boolean => {
		const txtLeft = (left.text ?? '').toLowerCase();
		const txtRight = (right.text ?? '').toLowerCase();
		return txtLeft === txtRight;
	};

	const oldParagraphsComp = splitTextElements(oldParagraph.elements);

	const newParagraphsComp = splitTextElements(newParagraph.elements);

	const arrCompareTextResult = diffArrays(
		oldParagraphsComp,
		newParagraphsComp,
		{comparator: comparatorText},
	);

	const oldElementsResults = arrCompareTextResult.filter(
		x => x.added === false,
	);

	const newElementsResults = arrCompareTextResult.filter(
		x => x.removed === false,
	);

	const oldElementsModified = combineMarkTextElements(oldElementsResults, true);

	const newElementsModified = combineMarkTextElements(
		newElementsResults,
		false,
	);

	const resultType =
		determineChangeStatus(oldElementsResults) ||
		determineChangeStatus(newElementsResults)
			? CompareResult.Changed
			: CompareResult.Equal;

	return {
		oldParagraphModified: {
			...oldParagraph,
			elements: oldElementsModified,
		},
		newParagraphModified: {...newParagraph, elements: newElementsModified},
		resultType,
	};
}

export function compareParagraphs(
	oldDocumentParagraphs: IParagraphCompareInput[],
	newDocumentParagraphs: IParagraphCompareInput[],
): IComparedItem[] {
	return compareVersions(oldDocumentParagraphs, newDocumentParagraphs);
}

const getTableOrImageElementTypes = (): ParagraphElementType[] => {
	return [
		ParagraphElementType.HtmlTable,
		ParagraphElementType.Image,
		ParagraphElementType.Table,
	];
};

const getIfIsTableOrImageElement = (element: Element): boolean => {
	const tableOrImageTypes: ParagraphElementType[] =
		getTableOrImageElementTypes();
	return tableOrImageTypes.includes(element.type);
};

const getIfElementHasTableOrImage = (element: Element): boolean => {
	const isTableOrImgElement: boolean = getIfIsTableOrImageElement(element);
	/**
	 * We check if it has an asset because legacy paragraphs can have text
	 * elements that have assets, which means they are displayed as images
	 * instead. Whether or not the asset's URI is empty does not matter because
	 * EditorContent will render it as an image anyways. So, we must consider it
	 * an image here, too.
	 */
	return isTableOrImgElement || Boolean(element.asset);
};

/**
 * Notes:
 *
 * - idx = index
 */
export function compareVersions(
	oldDocumentParagraphs: IParagraphCompareInput[],
	newDocumentParagraphs: IParagraphCompareInput[],
): IComparedItem[] {
	let idxOld = 0;
	let idxNew = 0;

	const result: IComparedItem[] = [];

	const arrEnumerationsOld = oldDocumentParagraphs.map(x =>
		x.enumeration.toLowerCase().replace(/[^a-z0-9]/gi, ''),
	);
	const arrEnumerationsNew = newDocumentParagraphs.map(x =>
		x.enumeration.toLowerCase().replace(/[^a-z0-9]/gi, ''),
	);

	const arrCompareResult = evaluateCompareResult(
		arrEnumerationsOld,
		arrEnumerationsNew,
	);

	const funcGenerateResults = (meyerDiff: ArrayChange<string>) => {
		if (meyerDiff.added === false && meyerDiff.removed === false) {
			meyerDiff.value.forEach(x => {
				const oldParagraph = oldDocumentParagraphs[idxOld];
				const newParagraph = newDocumentParagraphs[idxNew];

				const {oldParagraphModified, newParagraphModified, resultType} =
					cmpText(oldParagraph, newParagraph);

				result.push({
					oldParagraph: oldParagraphModified,
					newParagraph: newParagraphModified,
					resultType: checkBothNotDismantled(
						arrEnumerationsOld,
						arrEnumerationsNew,
					)
						? CompareResult.Changed
						: resultType,
				});

				idxOld++;
				idxNew++;
			});
		}

		if (meyerDiff.removed === true) {
			meyerDiff.value.forEach(x => {
				const oldParagraph = oldDocumentParagraphs[idxOld];
				result.push({
					oldParagraph,
					resultType: CompareResult.Deleted,
				});
				idxOld++;
			});
		}

		if (meyerDiff.added === true) {
			meyerDiff.value.forEach(x => {
				const newParagraph = newDocumentParagraphs[idxNew];
				result.push({
					newParagraph,
					resultType: CompareResult.New,
				});
				idxNew++;
			});
		}
	};

	arrCompareResult.forEach(funcGenerateResults);

	return result;
}

function evaluateCompareResult(
	arrEnumerationsOld: string[],
	arrEnumerationsNew: string[],
) {
	if (checkBothNotDismantled(arrEnumerationsOld, arrEnumerationsNew)) {
		return [
			{
				added: false,
				removed: false,
				count: 1,
				value: [arrEnumerationsOld.at(0)!],
			},
		];
	}
	return diffArrays(arrEnumerationsOld, arrEnumerationsNew);
}

function checkBothNotDismantled(
	arrEnumerationsOld: string[],
	arrEnumerationsNew: string[],
) {
	return arrEnumerationsOld.length === 1 && arrEnumerationsNew.length === 1;
}

function splitTextElements(elements: Element[]): Element[] {
	return elements.flatMap(obj => {
		if (obj.type === ParagraphElementType.Text && obj.isHeader === false) {
			return (obj.text ?? '').split('').map(char => ({
				...obj,
				text: char,
			}));
		}
		return [{...obj}];
	});
}

function combineMarkTextElements(
	elementsResults: ArrayChange<Element>[],
	markRemoved: boolean,
): Element[] {
	const combinedElements = elementsResults.map(x => {
		const combinedArray = Object.values(
			x.value.reduce((acc, obj) => {
				if (
					acc.length > 0 &&
					acc.at(-1)?.isHeader === obj.isHeader &&
					acc.at(-1)?.type === ParagraphElementType.Text &&
					obj.type === ParagraphElementType.Text
				) {
					acc[acc.length - 1].text = `${acc.at(-1)?.text}${obj.text}`;
				} else {
					acc.push(obj as Element);
				}
				return acc;
			}, [] as Element[]),
		);
		return {...x, value: combinedArray};
	});

	return combinedElements
		.map(x => {
			if (x.removed === markRemoved && x.added === !markRemoved) {
				return {
					...x,
					value: x.value.map(y => {
						return {...y, text: `<mark>${y.text}</mark>`};
					}),
				};
			}
			return x;
		})
		.flatMap(x => x.value);
}

function determineChangeStatus(
	elementsResults: ArrayChange<Element>[],
): boolean {
	return (
		elementsResults
			.filter(x => x.added || x.removed)
			.flatMap(x => x.value)
			.filter(x => /^[0-9a-zA-Z]+$/.test(x.text ?? '.')).length > 0
	);
}
